/**
 * mock-data.ts — the SINGLE canned-data source for the whole public demo.
 *
 * Everything a prospect sees comes from here. Per the isolation invariants it is
 * fiction: it lives in memory and resets on reload. Nothing here is PHI.
 *
 * The shapes mirror the REAL hierarchy the product records so the demo teaches the
 * real structure:  Client → Session → Theme → Specific Topic → Target Emotion →
 * Check-ins → Settings snapshot.  (The live, client-tied history feature is built
 * in a separate session — see /HISTORY_FIX_HANDOFF.md.)
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * 👉 JAKE — THIS IS YOURS TO SHAPE (the demo's sales narrative).
 *    The data below WORKS but is generic. Refine the story: realistic (still
 *    fictional) topics, believable pre→post arcs, and client labels that feel like
 *    how a therapist actually names clients. The "Demo — sample data only" banner
 *    keeps everything unambiguously fake.
 * ───────────────────────────────────────────────────────────────────────────────
 */

export type Modality = "EMDR" | "ART" | "Other";

/** Session theme categories (mirrors the real session-topic taxonomy). */
export const THEMES = ["PTSD", "Phobia", "Grief", "Anxiety", "Pain", "Performance", "Other"] as const;
export type Theme = (typeof THEMES)[number];

/**
 * A per-session settings SNAPSHOT — and the shape of a client's SAVED PREFERENCES
 * (the settings that auto-apply when you start a session for that client).
 */
export interface SessionSettings {
  ballColor: string;
  canvasBg: string;
  hz: number;
  audioOn: boolean;
  audioPreset: string; // "theta" | "alpha" | "delta" | "off"
  audioVolume: number; // 0..1
  hapticsOn: boolean;
  hapticsIntensity: number; // 0..1
  videoOn: boolean;
}

export const DEFAULT_SETTINGS: SessionSettings = {
  ballColor: "#5BA9F7",
  canvasBg: "#0D1217",
  hz: 0.8,
  audioOn: false,
  audioPreset: "theta",
  audioVolume: 0.27,
  hapticsOn: false,
  hapticsIntensity: 0.8,
  videoOn: false,
};

export interface CheckinSnapshot {
  // 1..10, or null when the scale was skipped (ScaleInput is a 1–10 picker — 0 is
  // unpickable, so null must stay null and never be coerced to a fake 0 reading).
  distress: number | null;
  capability: number | null;
  emotions: string[];
  goal?: string; // pre-session intention
  note?: string; // post-session note
}

export interface DemoSession {
  id: string;
  clientId: string; // saved sessions ALWAYS have a client (the guardrail teaches why)
  date: string; // ISO date
  durationMin: number;
  modality: Modality;
  theme: Theme | string; // category
  topic: string; // specific topic, e.g. "Recent fight with my brother"
  targetEmotion: string; // the stubborn emotion worked, e.g. "Anger"
  settings: SessionSettings; // snapshot of how the session was run
  pre: CheckinSnapshot;
  post: CheckinSnapshot;
  followUp?: { dueDate: string; note?: string; distress?: number };
}

export interface DemoClient {
  id: string;
  /** Pseudonymous label — how a therapist tags a client. */
  displayName: string;
  dob: string; // ISO
  state: string; // US state code — drives the state-aware consent path
  consentStatus: "signed" | "pending";
  modality: Modality;
  savedPrefs: SessionSettings;
}

export const DEMO_THERAPIST = {
  name: "Dr. Demo Sample, LCSW",
  clinic: "Sample Practice (demo)",
  // Inert account-preference fields surfaced on the Settings tab. Obviously-fake
  // and non-routable — nothing here is ever contacted or persisted.
  email: "demo@sample-practice.example",
  timezone: "America/Los_Angeles",
} as const;

/** Seed roster. Danna R. is the narrative hero; one minor exercises the consent path. */
export const DEMO_CLIENTS: DemoClient[] = [
  {
    id: "c-danna",
    displayName: "Danna R.",
    dob: "1989-07-14",
    state: "WA",
    consentStatus: "signed",
    modality: "EMDR",
    savedPrefs: { ballColor: "#E5B970", canvasBg: "#0D1217", hz: 0.9, audioOn: true, audioPreset: "theta", audioVolume: 0.3, hapticsOn: true, hapticsIntensity: 0.7, videoOn: false },
  },
  {
    id: "c-avery",
    displayName: "Avery Example",
    dob: "1991-04-12",
    state: "WA",
    consentStatus: "signed",
    modality: "EMDR",
    savedPrefs: { ...DEFAULT_SETTINGS, ballColor: "#5BA9F7", hz: 0.8, audioOn: true, audioPreset: "alpha" },
  },
  {
    id: "c-jordan",
    displayName: "Jordan Placeholder",
    dob: "1987-11-02",
    state: "OR",
    consentStatus: "signed",
    modality: "EMDR",
    savedPrefs: { ...DEFAULT_SETTINGS, ballColor: "#8FBC93", hz: 1.0 },
  },
  {
    id: "c-sam",
    displayName: "Sam Sample (minor)",
    dob: "2011-02-20",
    state: "WA",
    consentStatus: "pending",
    modality: "ART",
    savedPrefs: { ...DEFAULT_SETTINGS },
  },
];

/** Canned per-client session history showing the Client→Session→Theme→Topic→Emotion arc. */
export const DEMO_SESSIONS: DemoSession[] = [
  {
    id: "s-danna-2",
    clientId: "c-danna",
    date: "2026-06-20",
    durationMin: 50,
    modality: "EMDR",
    theme: "PTSD",
    topic: "Recent fight with my brother",
    targetEmotion: "Anger",
    settings: DEMO_CLIENTS[0].savedPrefs,
    pre: { distress: 8, capability: 3, emotions: ["angry", "tense", "on edge"], goal: "Let go of the anger that keeps replaying" },
    post: { distress: 3, capability: 7, emotions: ["calmer", "clearer", "relieved"], note: "The memory lost its charge — client recalled it without the jaw-clench." },
    followUp: { dueDate: "2026-06-27", note: "Check whether the calm held through the week." },
  },
  {
    id: "s-danna-1",
    clientId: "c-danna",
    date: "2026-06-06",
    durationMin: 45,
    modality: "EMDR",
    theme: "PTSD",
    topic: "The phone call that started it",
    targetEmotion: "Fear",
    settings: DEMO_CLIENTS[0].savedPrefs,
    pre: { distress: 7, capability: 4, emotions: ["fearful", "frozen"], goal: "Stop bracing every time the phone rings" },
    post: { distress: 4, capability: 6, emotions: ["steadier"], note: "Good progress; some residual startle remains." },
    followUp: { dueDate: "2026-06-13" },
  },
  {
    id: "s-avery-1",
    clientId: "c-avery",
    date: "2026-06-13",
    durationMin: 50,
    modality: "EMDR",
    theme: "Phobia",
    topic: "Reprocessing a driving-related memory",
    targetEmotion: "Fear",
    settings: DEMO_CLIENTS[1].savedPrefs,
    pre: { distress: 8, capability: 3, emotions: ["anxious", "overwhelmed", "tense"], goal: "Feel safe behind the wheel again" },
    post: { distress: 3, capability: 7, emotions: ["calm", "relieved", "hopeful"], note: "Client reported the image felt 'further away'." },
    followUp: { dueDate: "2026-06-20" },
  },
  {
    id: "s-jordan-1",
    clientId: "c-jordan",
    date: "2026-06-09",
    durationMin: 40,
    modality: "EMDR",
    theme: "Performance",
    topic: "Performance anxiety before presentations",
    targetEmotion: "Self-doubt",
    settings: DEMO_CLIENTS[2].savedPrefs,
    pre: { distress: 7, capability: 4, emotions: ["nervous", "self-critical"], goal: "Speak without the racing heart" },
    post: { distress: 4, capability: 6, emotions: ["steadier", "focused"], note: "Capability up; client wants to continue next week." },
  },
];

/** Sessions for a client, newest first. */
export function sessionsForClient(sessions: DemoSession[], clientId: string): DemoSession[] {
  return sessions.filter((s) => s.clientId === clientId).sort((a, b) => (a.date < b.date ? 1 : -1));
}

/** Latest session date for a client, or null if none. */
export function lastSessionDate(sessions: DemoSession[], clientId: string): string | null {
  return sessionsForClient(sessions, clientId)[0]?.date ?? null;
}

/** Header label for the session view's script panel. */
export const DEMO_SESSION_TOPIC = "Sample protocol · desensitization phase";

/** Fixed room code that bridges the therapist broadcast → client mirror in the
 *  same tab (via the loopback socket stub) for the perspective toggle. */
export const DEMO_SESSION_CODE = "demo-session";
